import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SpringElement, type SpringElementControls } from './ui/spring-element';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';

// 原本放在 Hero 里的导航栏。Hero 现在被包在 sticky + transform 的容器里，
// fixed 定位会失效，所以把导航抽到 App 顶层单独渲染。
const AVATAR_HINT_KEY = 'krys:avatar-drag-hint';
// Books 页面暂时隐藏；要放出来把这里改成 true 即可
const SHOW_BOOKS = false;

const markHintSeen = () => {
  try {
    window.localStorage.setItem(AVATAR_HINT_KEY, '1');
  } catch {
    // 写不进去就算了，不影响交互
  }
};

/**
 * 首次访问时自动"拉"一下头像再松手，让访客看到弹簧效果。
 * 独立成组件：演示/提示的状态变化只重渲染这里，不会波及 SpringElement 的拖拽。
 */
const AvatarDragDemo: React.FC<{
  controls: React.MutableRefObject<SpringElementControls | null>;
  target: React.RefObject<HTMLDivElement | null>;
}> = ({ controls, target }) => {
  const [showHint, setShowHint] = useState(false);
  const cancelled = useRef(false);

  useEffect(() => {
    let seen = true;
    try {
      seen = window.localStorage.getItem(AVATAR_HINT_KEY) === '1';
    } catch {
      // 隐私模式读不到 localStorage，干脆不演示，避免每次都来一遍
    }
    // 调试/展示用：URL 带 ?demo=1 强制演示一次
    const forced = new URLSearchParams(window.location.search).get('demo') === '1';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if ((seen && !forced) || reduceMotion) return;

    const el = target.current;
    const timers: number[] = [];
    let raf = 0;

    // 用户自己上手了：立刻停掉演示，把位移交还给真实拖拽
    const cancel = () => {
      cancelled.current = true;
      cancelAnimationFrame(raf);
      const c = controls.current;
      if (c) {
        c.x.set(0);
        c.y.set(0);
      }
      setShowHint(false);
      markHintSeen();
    };
    el?.addEventListener('pointerdown', cancel, { passive: true });

    // 手动逐帧喂位移，和真实拖拽走的是同一条路（x.set / y.set），弹簧的响应完全一致
    const tween = (
      from: { x: number; y: number },
      to: { x: number; y: number },
      duration: number,
      shape: (t: number) => { x: number; y: number },
    ) =>
      new Promise<void>((resolve) => {
        const t0 = performance.now();
        const step = (now: number) => {
          const c = controls.current;
          if (cancelled.current || !c) return resolve();
          const t = Math.min(1, (now - t0) / duration);
          const p = shape(t);
          c.x.set(from.x + (to.x - from.x) * p.x);
          c.y.set(from.y + (to.y - from.y) * p.y);
          if (t < 1) raf = requestAnimationFrame(step);
          else resolve();
        };
        raf = requestAnimationFrame(step);
      });

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const runDemo = async () => {
      const c = controls.current;
      if (!c || cancelled.current) return;
      markHintSeen();
      c.updateCenter();

      const rest = { x: 0, y: 0 };
      const held = { x: 64, y: 40 };

      // 1) 被人捏住拉出去：先快后慢；y 比 x 慢半拍，走出一条弧线而不是直线
      await tween(rest, held, 720, (t) => ({
        x: easeOutCubic(t),
        y: easeOutCubic(Math.max(0, (t - 0.08) / 0.92)),
      }));
      if (cancelled.current) return;

      // 2) 拉到头绷住：手在抖，抖动逐渐收小
      await tween(held, held, 560, (t) => {
        const decay = 1 - t;
        return {
          x: 1 + (Math.sin(t * 24) * 3.5 * decay) / held.x,
          y: 1 + (Math.sin(t * 19 + 1.2) * 2.5 * decay) / held.y,
        };
      });
      if (cancelled.current) return;

      // 3) 松手：和真实拖拽结束时完全一样，交给弹簧自己弹回去
      c.x.set(0);
      c.y.set(0);

      // 弹回去之后再给一句"轮到你了"
      timers.push(window.setTimeout(() => {
        if (!cancelled.current) setShowHint(true);
      }, 500));
      timers.push(window.setTimeout(() => setShowHint(false), 4500));
    };

    // 等导航栏自己的入场动画走完；如果标签页在后台（rAF 会被暂停），等切回来再演示
    const scheduleDemo = () => timers.push(window.setTimeout(runDemo, 1600));
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      document.removeEventListener('visibilitychange', onVisible);
      scheduleDemo();
    };
    if (document.visibilityState === 'visible') scheduleDemo();
    else document.addEventListener('visibilitychange', onVisible);

    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      cancelAnimationFrame(raf);
      el?.removeEventListener('pointerdown', cancel);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [controls, target]);

  return (
    <AnimatePresence>
      {showHint && (
        <motion.div
          className="absolute left-1/2 top-full mt-3 -translate-x-1/2 pointer-events-none z-[60]"
          initial={{ opacity: 0, y: -6, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.94, transition: { duration: 0.2 } }}
          transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <motion.div
            animate={{ y: [0, -3, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div className="w-2 h-2 bg-[#F1EFE0] rotate-45 mx-auto -mb-1" />
            <div className="whitespace-nowrap rounded-full bg-[#F1EFE0] px-3 py-1.5 text-[11px] sm:text-xs font-medium text-[#2C2721] shadow-lg">
              Your turn
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const SiteNavigation: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const springControls = useRef<SpringElementControls | null>(null);
  const avatarWrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Navigation Header */}
      <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-sm border-b transition-all duration-300 ${
        isScrolled ? 'bg-black/95 border-gray-600/20' : 'bg-transparent border-gray-600/20'
      }`}>
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <motion.div
              className="flex items-center gap-3"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative flex" ref={avatarWrapRef}>
                <SpringElement springClassName="stroke-white" controlsRef={springControls}>
                  <Avatar className="w-10 h-10 sm:w-12 sm:h-12">
                    <AvatarImage
                      draggable={false}
                      src="/photo/Touxiang.jpg"
                      alt="Krys"
                    />
                    <AvatarFallback>K</AvatarFallback>
                  </Avatar>
                </SpringElement>

                <AvatarDragDemo controls={springControls} target={avatarWrapRef} />
              </div>
              <div className="text-xl sm:text-2xl font-bold text-[#F1EFE0]">Krys</div>
            </motion.div>
            {/* Mobile menu button */}
            <button
              className="md:hidden text-white z-50"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Desktop menu */}
            <ul className="hidden md:flex space-x-4 lg:space-x-8">
              <li><a href="#skills" className="text-[#F1EFE0] hover:text-gray-300 transition-all duration-300 hover:-translate-y-0.5" onClick={(e) => {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent('openSkills'));
              }}>Skills</a></li>
              <li><a href="#education" className="text-[#F1EFE0] hover:text-gray-300 transition-all duration-300 hover:-translate-y-0.5" onClick={(e) => {
                e.preventDefault();
                document.getElementById('education')?.scrollIntoView({ behavior: 'smooth' });
              }}>Education</a></li>
              <li><a href="#experience" className="text-[#F1EFE0] hover:text-gray-300 transition-all duration-300 hover:-translate-y-0.5" onClick={(e) => {
                e.preventDefault();
                document.getElementById('experience')?.scrollIntoView({ behavior: 'smooth' });
              }}>Experience</a></li>
              <li><a href="#publications" className="text-[#F1EFE0] hover:text-gray-300 transition-all duration-300 hover:-translate-y-0.5" onClick={(e) => {
                e.preventDefault();
                document.getElementById('publications')?.scrollIntoView({ behavior: 'smooth' });
              }}>Publications</a></li>
              <li><a href="#game" className="text-[#F1EFE0] hover:text-gray-300 transition-all duration-300 hover:-translate-y-0.5" onClick={(e) => {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent('openGame'));
              }}>Game</a></li>
              {SHOW_BOOKS && (
                <li><a href="#books" className="text-[#F1EFE0] hover:text-gray-300 transition-all duration-300 hover:-translate-y-0.5" onClick={(e) => {
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent('openBookReader'));
                }}>Books</a></li>
              )}
            </ul>
          </div>
        </div>

        {/* Mobile menu overlay */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Mobile menu dropdown - Right Side */}
        <motion.div
          className={`md:hidden fixed right-0 top-[60px] h-[calc(100vh-60px)] w-72 bg-black/95 backdrop-blur-md border-l border-gray-600/20 ${isMobileMenuOpen ? 'block' : 'hidden'} overflow-y-auto z-50`}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: isMobileMenuOpen ? 1 : 0, x: isMobileMenuOpen ? 0 : 20 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex flex-col h-full">
            {/* Avatar Section */}
            <div className="flex flex-col items-center py-6 border-b border-gray-600/20">
              <Avatar className="w-20 h-20 mb-3">
                <AvatarImage
                  draggable={false}
                  src="/photo/Touxiang.jpg"
                  alt="Krys"
                />
                <AvatarFallback>K</AvatarFallback>
              </Avatar>
              <h3 className="text-xl font-bold text-[#F1EFE0]" style={{fontFamily: 'Vixa-Regular, sans-serif'}}>Krys</h3>
              <p className="text-sm text-gray-400">Software Engineer</p>
            </div>

            {/* Menu Items */}
            <ul className="flex-grow py-4 px-6">
              <li className="py-3">
                <a
                  href="#skills"
                  className="block text-[#F1EFE0] hover:text-gray-300 transition-all duration-300 text-lg"
                  onClick={(e) => {
                    e.preventDefault();
                    window.dispatchEvent(new CustomEvent('openSkills'));
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Skills
                </a>
              </li>
              <li className="py-3">
                <a
                  href="#education"
                  className="block text-[#F1EFE0] hover:text-gray-300 transition-all duration-300 text-lg"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('education')?.scrollIntoView({ behavior: 'smooth' });
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Education
                </a>
              </li>
              <li className="py-3">
                <a
                  href="#experience"
                  className="block text-[#F1EFE0] hover:text-gray-300 transition-all duration-300 text-lg"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('experience')?.scrollIntoView({ behavior: 'smooth' });
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Experience
                </a>
              </li>
              <li className="py-3">
                <a
                  href="#publications"
                  className="block text-[#F1EFE0] hover:text-gray-300 transition-all duration-300 text-lg"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('publications')?.scrollIntoView({ behavior: 'smooth' });
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Publications
                </a>
              </li>
              <li className="py-3">
                <a
                  href="#game"
                  className="block text-[#F1EFE0] hover:text-gray-300 transition-all duration-300 text-lg"
                  onClick={(e) => {
                    e.preventDefault();
                    window.dispatchEvent(new CustomEvent('openGame'));
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Game
                </a>
              </li>
              {SHOW_BOOKS && (
                <li className="py-3">
                  <a
                    href="#books"
                    className="block text-[#F1EFE0] hover:text-gray-300 transition-all duration-300 text-lg"
                    onClick={(e) => {
                      e.preventDefault();
                      window.dispatchEvent(new CustomEvent('openBookReader'));
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    Books
                  </a>
                </li>
              )}
            </ul>

            {/* Social Links */}
            <div className="py-6 px-6 border-t border-gray-600/20">
              <p className="text-sm text-gray-400 mb-4">Connect with me</p>
              <div className="flex space-x-4">
                <a
                  href="https://www.linkedin.com/in/ziqi-gao-8b76522b2"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-[#F1EFE0] text-[#F1EFE0] hover:bg-[#F1EFE0] hover:text-[#2C2721] flex items-center justify-center transition-all duration-300"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                </a>
                <a
                  href="https://github.com/KrysG-2004"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-[#F1EFE0] text-[#F1EFE0] hover:bg-[#F1EFE0] hover:text-[#2C2721] flex items-center justify-center transition-all duration-300"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </nav>
    </>
  );
};

export default SiteNavigation;
